import type { Spec } from "@json-render/core";
import { vizRuntimeCatalog } from "@/lib/ai/visualization/catalog";
import { buildVizDatasetRowsPath } from "@/lib/ai/visualization/config";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";
import { projectDatasetForRender } from "@/lib/ai/visualization/engine/render-dataset-projection";
import type { PromptOutputLanguage } from "@/lib/ai/visualization/spec/authoring-request";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import { buildVizElementFromSpec } from "@/lib/ai/visualization/views/registry";
import { compileTableSpec } from "@/lib/ai/visualization/views/table/logic";

/**
 * compile / catalog failure で table fallback を試してよい reason code。
 */
const TABLE_FALLBACK_ELIGIBLE_REASONS = new Set<string>([
  "invalid-binding",
  "invalid-model-spec",
  "invalid-tree",
  "invalid-data-path",
  "invalid-chart-structure",
]);

/**
 * failure summary / transport へ流す reason code の短い説明。
 */
const VIZ_REASON_SUMMARIES: Record<string, string> = {
  "missing-tool-call-id": "viz_render is missing toolCallId",
  "missing-spec-model": "visualization model is not configured",
  "no-snapshots": "no analytics tool outputs are available",
  "no-datasets": "no visualization datasets could be derived",
  "authoring-timeout": "visualization authoring timed out",
  "authoring-parse-failed":
    "visualization authoring output could not be parsed",
  "invalid-dataset-id": "selected datasetId does not exist",
  "invalid-view-type": "selected view type is invalid",
  "missing-dataset": "dataset was not found during compile",
  "invalid-binding": "field bindings do not match the dataset",
  "invalid-model-spec": "compiled visualization spec is invalid",
  "invalid-tree": "visualization tree failed runtime validation",
  "invalid-data-path": "visualization dataPath is invalid",
  "invalid-chart-structure": "visualization root/child structure is invalid",
  "narrative-timeout": "card narrative synthesis timed out",
  "narrative-parse-failed": "card narrative output could not be parsed",
};

/**
 * reason code を user/model-facing failure summary 向けの短い説明へ正規化する。
 */
export function summarizeVizFailureReason(reason: string): string {
  return VIZ_REASON_SUMMARIES[reason] ?? reason;
}

function buildFailureMessage(args: {
  reason: string;
  detail?: string | null;
}): string {
  const reasonSummary = summarizeVizFailureReason(args.reason);
  const detail =
    typeof args.detail === "string" && args.detail.trim().length > 0
      ? args.detail.trim()
      : null;
  return detail ? `${reasonSummary}: ${detail}` : reasonSummary;
}

/**
 * table fallback 成立時に「データは表で表示済み」と親モデルへ伝える短い補足。
 *
 * @remarks
 * 親モデルが「描画失敗」だけを読んで謝罪・再試行しないよう、failure summary に
 * 添える。`buildChartRenderFailureNotice` の表表示クライズと文言をそろえる。
 */
export function buildTableFallbackSubstitutionNote(
  outputLanguage?: PromptOutputLanguage,
): string {
  if (outputLanguage === "Japanese") {
    return "データは表形式で表示済みです。";
  }
  return "The dataset is shown as a table instead.";
}

/**
 * chart 描画失敗時に card へ出す system-side notice を組み立てる。
 */
export function buildChartRenderFailureNotice(args: {
  reason: string;
  outputLanguage?: PromptOutputLanguage;
  detail?: string | null;
}): string {
  const reasonSummary = summarizeVizFailureReason(args.reason);
  if (args.outputLanguage === "Japanese") {
    const detail =
      typeof args.detail === "string" && args.detail.trim().length > 0
        ? ` (${args.detail.trim()})`
        : "";
    return `チャートの描画に失敗しました（${reasonSummary}${detail}）。データを表形式で表示しています。`;
  }

  const detail =
    typeof args.detail === "string" && args.detail.trim().length > 0
      ? `: ${args.detail.trim()}`
      : "";
  return `Chart rendering failed (${reasonSummary}${detail}). Showing the dataset as a table instead.`;
}

/**
 * 可視化不能時に canonical output shape を保つための非表示 tree を生成する。
 */
export function buildFallbackTree(): Spec {
  return {
    root: "viz-fallback-card",
    elements: {
      "viz-fallback-card": {
        type: "VizCard",
        props: {
          title: "Visualization unavailable",
          description: null,
          footnote: null,
        },
        // 失敗は tool result の詳細へ集約し、独立した fallback card は描画しない。
        visible: false,
        children: [],
      },
    },
  };
}

export type TableFallbackBuildResult = {
  tree: Spec;
  spec: VizSpec;
  datasetForRender: VizDataset;
  notice: string;
  summary: string;
};

function isTableFallbackDataset(
  dataset: VizDataset | null | undefined,
): dataset is VizDataset {
  if (!dataset) {
    return false;
  }
  return (
    dataset.rows.length > 0 && dataset.profile.supportedViews.includes("table")
  );
}

/**
 * chart compile / render が失敗したとき、valid dataset から deterministic table fallback を試みる。
 */
export function tryBuildTableFallbackOutput(args: {
  dataset: VizDataset | null | undefined;
  datasetId: string;
  reason: string;
  outputLanguage?: PromptOutputLanguage;
  cardTitle?: string | null;
  failureDetail?: string | null;
}): TableFallbackBuildResult | null {
  if (!isTableFallbackDataset(args.dataset)) {
    return null;
  }
  const dataset = args.dataset;

  const notice = buildChartRenderFailureNotice({
    reason: args.reason,
    outputLanguage: args.outputLanguage,
    detail: args.failureDetail,
  });
  const summary = `${buildFailureMessage({
    reason: args.reason,
    detail: args.failureDetail,
  })} ${buildTableFallbackSubstitutionNote(args.outputLanguage)}`;
  const cardTitle =
    typeof args.cardTitle === "string" && args.cardTitle.trim().length > 0
      ? args.cardTitle.trim()
      : "Data table";

  const tableSpec = compileTableSpec({
    datasetId: args.datasetId,
    dataset,
    card: {
      title: cardTitle,
      description: notice,
      footnote: null,
    },
    view: {
      columns: [],
      sort: null,
      limit: null,
    },
  });

  let projected;
  try {
    projected = projectDatasetForRender({
      dataset,
      spec: tableSpec,
    });
  } catch {
    return null;
  }

  const chartKey = "viz-chart";
  const built = buildVizElementFromSpec({
    spec: projected.spec,
    dataset: projected.dataset,
    dataPath: buildVizDatasetRowsPath(args.datasetId),
    chartKey,
    tableKey: chartKey,
  });

  const tree: Spec = {
    root: "viz-card",
    elements: {
      "viz-card": {
        type: "VizCard",
        props: {
          title: cardTitle,
          description: notice,
          footnote: null,
        },
        visible: true,
        children: [built.elementKey],
      },
      [built.elementKey]: built.element,
    },
  };

  const catalogValidation = vizRuntimeCatalog.validate(tree);
  if (!catalogValidation.success || !catalogValidation.data) {
    return null;
  }

  return {
    tree: catalogValidation.data as Spec,
    spec: projected.spec,
    datasetForRender: projected.dataset,
    notice,
    summary,
  };
}

export function isTableFallbackEligibleReason(reason: string): boolean {
  return TABLE_FALLBACK_ELIGIBLE_REASONS.has(reason);
}
